import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faCircleCheck,
	faCircleExclamation,
	faCircleInfo,
	faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

/**
 * Inline callout box for warnings, errors, info, and success messages.
 *
 * Previously the distribution module had ~8 sites repeating
 * `bg-amber-50 dark:bg-amber-900/20 border border-amber-200 ...` (or
 * red, or blue, or yellow) inline. That made dark-mode polish a hunt
 * across multiple files and meant every redesign touched 8 places.
 *
 * This primitive consolidates them. Variants resolve to the shared
 * `callout*` class tokens, which carry fill, stroke and text colour
 * together, so dark-mode flips automatically and any future re-skin
 * happens in one spot.
 *
 * It used to pair a solid pale tint with `border-transparent`. That was
 * the treatment PD-15144 retired everywhere else, so leaving it here
 * would have given the product two different callout looks — the shared
 * component showing the old one.
 *
 * Use it via:
 *   <Callout variant="warning" title="..." icon={faTriangleExclamation}>
 *     ...body...
 *   </Callout>
 *
 * Optional `title` renders bold above the children. Optional `icon`
 * sits to the left of the content. Defaults pick a sensible icon per
 * variant.
 */

type CalloutVariant = 'info' | 'warning' | 'danger' | 'success';

interface CalloutProps {
	variant?: CalloutVariant;
	title?: string;
	icon?: IconDefinition;
	className?: string;
	children?: React.ReactNode;
}

const defaultIcon: Record<CalloutVariant, IconDefinition> = {
	info: faCircleInfo,
	warning: faTriangleExclamation,
	danger: faCircleExclamation,
	success: faCircleCheck,
};

export function Callout({
	variant = 'info',
	title,
	icon,
	className,
	children,
}: CalloutProps): React.ReactElement {
	const colors = useThemeColors();

	const calloutClass: string = ((): string => {
		switch (variant) {
			case 'warning':
				return colors.classes.calloutWarning;
			case 'danger':
				return colors.classes.calloutDanger;
			case 'success':
				return colors.classes.calloutSuccess;
			default:
				return colors.classes.calloutInfo;
		}
	})();

	const resolvedIcon = icon ?? defaultIcon[variant];

	return (
		<div
			className={cn(
				'flex items-start gap-3 p-3 rounded-md',
				// The token supplies the border, so the icon, title and body all
				// inherit one tone instead of each restating it.
				calloutClass,
				className,
			)}
			role={variant === 'danger' ? 'alert' : 'status'}
		>
			<FontAwesomeIcon
				icon={resolvedIcon}
				className="mt-0.5 h-4 w-4 flex-shrink-0"
			/>
			<div className="flex-1 min-w-0">
				{title != null && title !== '' && (
					<div className="text-sm font-semibold mb-0.5">{title}</div>
				)}
				{children != null && <div className="text-xs">{children}</div>}
			</div>
		</div>
	);
}
